import { all, call, fork, put, takeEvery, takeLatest } from '@redux-saga/core/effects';

import * as Actions from './actions';
import * as ActionsType from './constants';
import {
  failureMessage,
  fetchGetMethod,
  fetchPostMethod,
  GetErrorMessageFromStatus,
  readFailedResponse,
} from '../../helpers/api';
import { getLoggedInUser } from '../../helpers/authUtils';
import { apiServiceUrl } from '../../constants';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';
import { addNotification } from '../../helpers/notification';

const vpApiServiceUrl = `${apiServiceUrl}/ValueProposition`;
const followersApiServiceUrl = `${apiServiceUrl}/Followers`;

function* searchVpxVpExchangeList({
  payload: { organizationName,companyName, vpName, ownerName, groupId, codeId, hashtag, country },
}) {
  try {
    const user = getLoggedInUser();
    const body = {
      OrganizationName: organizationName,
      CompanyName: companyName,
      Name: vpName,
      OwnerName: ownerName,
      GroupId: groupId,
      CodeId: codeId,
      Hashtag: hashtag,
      Country: country,
    };
    const response = yield call(
      fetchPostMethod,
      `${vpApiServiceUrl}/SearchVpxVpExchangeList`,
      user.token,
      body
    );

    if (response.status === 200) {
      const { vpList, totalCount, truncated, status } = yield response.data
        .then((response) => {
          return response.status === 200
            ? {
                vpList: response.results,
                totalCount: response.totalCount ?? response.TotalCount ?? null,
                truncated: !!(response.truncated ?? response.Truncated),
              }
            : { status: response.status };
        })
        .catch(() => ({ status: 500 }));
      if (vpList) {
        yield put(Actions.searchVpxVpExchangeListSuccess(vpList, totalCount, truncated));
      } else {
        yield put(
          Actions.searchVpxVpExchangeListFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.searchVpxVpExchangeListFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.searchVpxVpExchangeListFailed(GetErrorMessageFromStatus(error)));
  }
}

function* vpViewUserProfile({ payload: { userId } }) {
  try {
    const user = getLoggedInUser();
    const body = { Id: userId };
    const response = yield call(
      fetchPostMethod,
      `${vpApiServiceUrl}/ViewUserProfile`,
      user.token,
      body
    );

    if (response.status === 200) {
      const { profile, status } = yield response.data
        .then((response) => ({
          profile: {
            Owner: response.owner ?? response.Owner,
            ValuePropositions: response.result ?? response.Result,
            TotalCount: response.totalCount ?? response.TotalCount ?? null,
            Truncated: !!(response.truncated ?? response.Truncated),
          },
        }))
        .catch(() => ({ status: 500 }));
      if (profile) {
        yield put(Actions.vpViewUserProfileSuccess(profile));
      } else {
        const failure = { status: status ?? 500, body: null };
        yield put(Actions.vpViewUserProfileFailed(failureMessage(failure), failure));
      }
    } else {
      const failure = yield call(readFailedResponse, response);
      yield put(Actions.vpViewUserProfileFailed(failureMessage(failure), failure));
    }
  } catch (error) {
    const failure = { status: 500 };
    yield put(Actions.vpViewUserProfileFailed(failureMessage(failure), failure));
  }
}

function* vpViewOrganizationProfile({ payload: { organizationId } }) {
  try {
    const user = getLoggedInUser();
    const body = { Id: organizationId };
    const response = yield call(
      fetchPostMethod,
      `${vpApiServiceUrl}/ViewOrganizationProfile`,
      user.token,
      body
    );
    if (response.status === 200) {
      const { organization, result, totalCount, truncated, status } = yield response.data
        .then((response) => ({
          organization: response.organization ?? response.Organization,
          result: response.result ?? response.Result,
          totalCount: response.totalCount ?? response.TotalCount ?? null,
          truncated: !!(response.truncated ?? response.Truncated),
        }))
        .catch(() => ({ status: 500 }));
      if (organization) {
        const profile = {
          Organization: organization,
          ValuePropositions: result,
          TotalCount: totalCount,
          Truncated: truncated,
        };
        yield put(Actions.vpViewOrganizationProfileSuccess(profile));
      } else {
        yield put(
          Actions.vpViewOrganizationProfileFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.vpViewOrganizationProfileFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.vpViewOrganizationProfileFailed(GetErrorMessageFromStatus(error)));
  }
}

function* loadExternalList({ payload: { permissionType } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      PermissionType: permissionType,
    };
    const response = yield call(
      fetchPostMethod,
      `${followersApiServiceUrl}/ExternalList`,
      user.token,
      body
    );

    if (response.status === 200) {
      const { status, externalList } = yield response.data
        .then((response) => ({ status: response.status, externalList: response.items }))
        .catch(() => ({ status: 500 }));
      if (status === 200 && !!externalList) {
        yield put(Actions.loadExternalListSuccess(externalList));
      } else {
        yield put(Actions.loadExternalListFailed(GetErrorMessageFromStatus({ status: status })));
      }
    } else {
      yield put(Actions.loadExternalListFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadExternalListFailed(GetErrorMessageFromStatus(error)));
  }
}

function* acceptToFollow({ payload: { vpId } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      ValuePropositionId: vpId,
    };
    const response = yield call(
      fetchPostMethod,
      `${followersApiServiceUrl}/AcceptToFollow`,
      user.token,
      body
    );

    if (response.status === 200) {
      const { status } = yield response.data
        .then((response) => ({ status: response.status }))
        .catch(() => ({ status: 500 }));
      if (status === 200) {
        yield put(Actions.acceptToFollowSuccess(vpId));
      } else {
        yield put(Actions.acceptToFollowFailed(GetErrorMessageFromStatus({ status: status })));
      }
    } else {
      yield put(Actions.acceptToFollowFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.acceptToFollowFailed(GetErrorMessageFromStatus(error)));
  }
}

// Followers endpoints answer HTTP 200 with a body whose own `status` says whether it worked.
function* postFollowerAction(url, vpId, { onSuccess, onFailed, title, fallback }) {
  let failed;
  try {
    const user = getLoggedInUser();
    const response = yield call(fetchPostMethod, url, user.token, { ValuePropositionId: vpId });
    const body = yield Promise.resolve(response.data).catch(() => null);
    if (response.status === 200 && body?.status === 200) {
      yield put(onSuccess(vpId));
      return;
    }
    failed = { status: response.status === 200 ? body?.status ?? 500 : response.status, body };
  } catch {
    // Network or unexpected error: no body to read, so the fallback sentence is shown.
  }
  let message = fallback;
  try {
    message = getApiErrorMessage(failed, fallback);
  } catch {
    // An unexpected body shape must not stop the failure being reported.
  }
  addNotification({ title, message, type: 'danger' });
  yield put(onFailed(message));
}

export function* requestToFollow({ payload: { vpId } }) {
  yield* postFollowerAction(`${followersApiServiceUrl}/RequestToFollow`, vpId, {
    onSuccess: Actions.requestToFollowSuccess,
    onFailed: Actions.requestToFollowFailed,
    title: "Follow didn't happen",
    fallback: "We couldn't follow this value proposition.",
  });
}

export function* requestToUnfollow({ payload: { vpId } }) {
  yield* postFollowerAction(`${followersApiServiceUrl}/Unfollow`, vpId, {
    onSuccess: Actions.requestToUnfollowSuccess,
    onFailed: Actions.requestToUnfollowFailed,
    title: "Unfollow didn't happen",
    fallback: "We couldn't unfollow this value proposition.",
  });
}

export function* watchSearchVpxVpExchangeList(): any {
  yield takeLatest(ActionsType.SEARCH_VPX_VP_EXCHANGE_LIST, searchVpxVpExchangeList);
}

export function* watchVpViewUserProfile(): any {
  yield takeLatest(ActionsType.VALUE_PROPOSITION_VIEW_USER_PROFILE, vpViewUserProfile);
}

export function* watchVpViewOrganizationProfile(): any {
  yield takeLatest(
    ActionsType.VALUE_PROPOSITION_VIEW_ORGANIZATION_PROFILE,
    vpViewOrganizationProfile
  );
}

export function* watchAcceptToFollow(): any {
  yield takeLatest(ActionsType.ACCEPT_TO_FOLLOW, acceptToFollow);
}

export function* watchRequestToFollow(): any {
  yield takeLatest(ActionsType.REQUEST_TO_FOLLOW, requestToFollow);
}

export function* watchRequestToUnfollow(): any {
  yield takeLatest(ActionsType.REQUEST_TO_UNFOLLOW, requestToUnfollow);
}

export function* watchLoadExternalList(): any {
  yield takeLatest(ActionsType.LOAD_EXTERNAL_LIST, loadExternalList);
}

function* vpViewUserProfileByShareKey({ payload: { shareKey } }) {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${vpApiServiceUrl}/GetProfileByShareKey?shareKey=${shareKey}`,
      user?.token
    );
    if (response.status === 200) {
      const data = yield response.data
        .then((d) => d)
        .catch(() => ({ status: 500 }));
      if (data && data.profile && data.profile.isPreview) {
        yield put(Actions.vpViewUserProfileByShareKeyPreviewSuccess(data.profile));
      } else if (data && (data.owner || data.Owner)) {
        yield put(Actions.vpViewUserProfileByShareKeyFullSuccess({
          Owner: data.owner ?? data.Owner,
          ValuePropositions: data.result ?? data.Result,
          TotalCount: data.totalCount ?? data.TotalCount ?? null,
          Truncated: !!(data.truncated ?? data.Truncated),
        }));
      } else {
        yield put(Actions.vpViewUserProfileByShareKeyFailed(GetErrorMessageFromStatus({ status: data?.status ?? 500 })));
      }
    } else {
      yield put(Actions.vpViewUserProfileByShareKeyFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.vpViewUserProfileByShareKeyFailed(GetErrorMessageFromStatus(error)));
  }
}

export function* watchVpViewUserProfileByShareKey(): any {
  yield takeEvery(ActionsType.VALUE_PROPOSITION_VIEW_USER_PROFILE_BY_SHARE_KEY, vpViewUserProfileByShareKey);
}

function* vpx(): any {
  yield all([
    fork(watchSearchVpxVpExchangeList),
    fork(watchVpViewUserProfile),
    fork(watchVpViewOrganizationProfile),
    fork(watchAcceptToFollow),
    fork(watchRequestToFollow),
    fork(watchRequestToUnfollow),
    fork(watchLoadExternalList),
    fork(watchVpViewUserProfileByShareKey),
  ]);
}

export default vpx;
